/* Contractor Portal — today's load tally + 7-day loads history (count and m³ only, never $). */
import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/mapTheme";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "2-digit" });

export default function ContractorLoads() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/contractor/loads?days=${days}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load history"));
  }, [days]);

  const today = new Date().toLocaleDateString("en-CA");
  const todayRow = data?.daily?.find((d) => d.date === today) || { loads: 0, m3: 0 };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-6xl" data-testid="contractor-loads-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">HIRE RECONCILIATION</div>
          <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">Loads <span className="text-[#38BDF8]">History</span></h1>
        </div>
        <div className="flex border border-[#2C313A]" data-testid="ctr-loads-range">
          {[7, 14, 31].map((n) => (
            <button key={n} data-testid={`ctr-loads-range-${n}`} onClick={() => setDays(n)} className={`px-3 py-1.5 mono text-xs ${days === n ? "bg-[#38BDF8] text-[#0B0D10] font-black" : "text-[#A1A6B0] hover:text-white"}`}>{n}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["Loads today", todayRow.loads, "ctr-loads-today"], ["m³ today", todayRow.m3, "ctr-m3-today"], [`Loads · ${days}d`, data?.totals?.loads ?? "—", "ctr-loads-range-total"], [`m³ · ${days}d`, data?.totals?.m3 ?? "—", "ctr-m3-range-total"]].map(([l, v, id]) => (
          <div key={id} className="bg-[#12151A] border border-[#22262E] px-4 py-3" data-testid={id}>
            <div className="text-[9px] tracking-widest uppercase text-[#8B909A]">{l}</div>
            <div className="heading text-2xl font-black mono text-white">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#12151A] border border-[#22262E] p-4" data-testid="ctr-loads-chart">
        <div className="text-[10px] tracking-widest uppercase text-[#8B909A] mb-3">m³ hauled per day</div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height={208}>
            <BarChart data={data?.daily || []} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="#22262E" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: "#8B909A", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8B909A", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F1216", border: "1px solid #2C313A", fontSize: 12 }} labelStyle={{ color: "#E5E7EB" }} formatter={(v, k) => [k === "m3" ? `${v} m³` : v, k === "m3" ? "Volume" : "Loads"]} />
              <Bar dataKey="m3" fill="#38BDF8" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="bg-[#12151A] border border-[#22262E]" data-testid="ctr-loads-by-truck">
          <div className="px-4 py-3 border-b border-[#22262E] text-[10px] tracking-widest uppercase text-[#8B909A]">By truck · {days}d</div>
          {(data?.by_truck || []).length === 0 && <div className="p-4 text-sm text-[#A1A6B0]">No loads in this range.</div>}
          {(data?.by_truck || []).map((t) => (
            <div key={t.truck_id} className="px-4 py-2.5 flex items-center justify-between border-b border-[#1B1F26] last:border-0" data-testid={`ctr-loads-truck-${t.number}`}>
              <span className="heading font-black text-white">{t.number}</span>
              <span className="mono text-xs text-[#A1A6B0]">{t.loads} loads · <span className="text-white">{t.m3} m³</span></span>
            </div>
          ))}
        </div>
        <div className="bg-[#12151A] border border-[#22262E] overflow-x-auto" data-testid="ctr-loads-dockets">
          <div className="px-4 py-3 border-b border-[#22262E] text-[10px] tracking-widest uppercase text-[#8B909A]">Load log</div>
          <table className="w-full text-xs">
            <thead className="text-[#8B909A] text-[10px] uppercase tracking-wider"><tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Truck</th><th className="text-left px-3 py-2">Plant</th><th className="text-left px-3 py-2">Site</th><th className="text-left px-3 py-2">Mix</th><th className="text-right px-3 py-2">m³</th><th className="text-left px-3 py-2">Loaded</th><th className="text-left px-3 py-2">Done</th><th className="text-left px-3 py-2">Status</th></tr></thead>
            <tbody>
              {(data?.dockets || []).map((d) => (
                <tr key={d.id} className="border-t border-[#1B1F26]" data-testid={`ctr-load-row-${d.docket_no}`}>
                  <td className="px-3 py-2 mono text-[#A1A6B0]">{d.date}</td>
                  <td className="px-3 py-2 heading font-black text-white">{d.truck_number}</td>
                  <td className="px-3 py-2 text-[#A1A6B0]">{d.plant}</td>
                  <td className="px-3 py-2 text-[#E5E7EB] max-w-[220px] truncate">{d.site_address}</td>
                  <td className="px-3 py-2 mono text-[#38BDF8]">{d.mix_code}</td>
                  <td className="px-3 py-2 mono text-right text-white">{d.quantity_m3}</td>
                  <td className="px-3 py-2 mono text-[#A1A6B0]">{fmtTime(d.loaded_at)}</td>
                  <td className="px-3 py-2 mono text-[#A1A6B0]">{fmtTime(d.completed_at)}</td>
                  <td className="px-3 py-2 text-[#A1A6B0]">{STATUS_LABEL[d.status] || d.status}</td>
                </tr>
              ))}
              {data && data.dockets.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-[#A1A6B0]">No loads recorded for your trucks in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
